import { useEffect, useState } from "react";
import { completeCoffeeOrder } from "./api";
import LandingNav from "./components/layout/LandingNav";
import { clearCart } from "./shop/cartStore";
import SiteFooter from "./components/ui/SiteFooter";
import { getStripePromise } from "./stripe";
import { translations } from "./translations";

function getConfirmLang() {
  if (typeof window === "undefined") return "hu";
  const lang = new URLSearchParams(window.location.search).get("lang");
  return lang === "en" ? "en" : "hu";
}

function getConfirmParams() {
  if (typeof window === "undefined") {
    return { status: "", paymentIntentId: "", redirectStatus: "" };
  }
  const params = new URLSearchParams(window.location.search);
  return {
    status: params.get("status") || "",
    paymentIntentId: params.get("payment_intent") || "",
    redirectStatus: params.get("redirect_status") || "",
  };
}

// Webshop checkouts come back with from=shop.
function isFromShop() {
  return new URLSearchParams(window.location.search).get("from") === "shop";
}

function CoffeeCheckoutConfirmPage() {
  const [lang, setLang] = useState(getConfirmLang);
  const [phase, setPhase] = useState("checking");
  const [detail, setDetail] = useState("");
  const siteT = translations[lang];
  const t = siteT.checkout;
  const fromShop = isFromShop();

  useEffect(() => {
    if (phase === "success" && fromShop) clearCart();
  }, [phase, fromShop]);

  useEffect(() => {
    document.title = `${t.confirmTitle} — Mizu Brunch & Coffee`;
    document.documentElement.lang = lang;
  }, [lang, t.confirmTitle]);

  useEffect(() => {
    let cancelled = false;

    async function resolveResult() {
      const { status, paymentIntentId, redirectStatus } = getConfirmParams();

      if (status === "success") {
        if (!cancelled) setPhase("success");
        return;
      }

      if (status === "error") {
        if (!cancelled) {
          setPhase("error");
          setDetail(t.paymentError);
        }
        return;
      }

      if (!paymentIntentId) {
        if (!cancelled) {
          setPhase("error");
          setDetail(t.confirmMissingPayment);
        }
        return;
      }

      try {
        const stripe = await getStripePromise();
        if (!stripe) {
          throw new Error("stripe_missing");
        }

        const clientSecret = new URLSearchParams(window.location.search).get(
          "payment_intent_client_secret",
        );

        if (!clientSecret) {
          if (redirectStatus === "succeeded") {
            try {
              await completeCoffeeOrder(paymentIntentId);
            } catch {
              // email may already be sent
            }
            if (!cancelled) setPhase("success");
            return;
          }
          throw new Error("missing_client_secret");
        }

        const { paymentIntent, error } = await stripe.retrievePaymentIntent(clientSecret);

        if (cancelled) return;

        if (error || !paymentIntent) {
          setPhase("error");
          setDetail(error?.message || t.paymentError);
          return;
        }

        if (paymentIntent.status === "succeeded") {
          try {
            await completeCoffeeOrder(paymentIntent.id);
          } catch {
            // fulfillment may already have run
          }
          setPhase("success");
          return;
        }

        if (
          paymentIntent.status === "processing" ||
          paymentIntent.status === "requires_capture"
        ) {
          setPhase("processing");
          return;
        }

        setPhase("error");
        setDetail(t.paymentError);
      } catch {
        if (!cancelled) {
          if (redirectStatus === "succeeded") {
            try {
              await completeCoffeeOrder(paymentIntentId);
            } catch {
              // ignore
            }
            setPhase("success");
          } else {
            setPhase("error");
            setDetail(t.paymentError);
          }
        }
      }
    }

    resolveResult();
    return () => {
      cancelled = true;
    };
  }, [t.confirmMissingPayment, t.paymentError]);

  function handleLangChange(nextLang) {
    setLang(nextLang);
    const url = new URL(window.location.href);
    url.searchParams.set("lang", nextLang);
    window.history.replaceState({}, "", url);
  }

  const homeHref = `/?lang=${lang}`;
  const checkoutHref = fromShop ? `/webshop/checkout?lang=${lang}` : `/checkout?lang=${lang}`;
  const shopHref = `/webshop?lang=${lang}`;

  return (
    <div className="landing checkout-page">
      <LandingNav
        lang={lang}
        onLangChange={handleLangChange}
        t={siteT}
        hrefPrefix="/"
      />

      <main className="checkout-page-main">
        <div
          className={[
            "checkout-confirm",
            phase === "success" ? "checkout-confirm--success" : "",
            phase === "error" ? "checkout-confirm--error" : "",
            phase === "processing" || phase === "checking"
              ? "checkout-confirm--pending"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {phase === "checking" ? (
            <>
              <p className="checkout-confirm-eyebrow">{t.confirmCheckingEyebrow}</p>
              <h1 className="checkout-confirm-title">{t.confirmCheckingTitle}</h1>
              <p className="checkout-confirm-copy">{t.loading}</p>
            </>
          ) : null}

          {phase === "processing" ? (
            <>
              <p className="checkout-confirm-eyebrow">{t.confirmProcessingEyebrow}</p>
              <h1 className="checkout-confirm-title">{t.confirmProcessingTitle}</h1>
              <p className="checkout-confirm-copy">{t.confirmProcessingCopy}</p>
              <div className="checkout-confirm-actions">
                <a className="checkout-confirm-btn checkout-confirm-btn--primary" href={homeHref}>
                  {t.confirmBackHome}
                </a>
              </div>
            </>
          ) : null}

          {phase === "success" ? (
            <>
              <p className="checkout-confirm-eyebrow">{t.confirmSuccessEyebrow}</p>
              <h1 className="checkout-confirm-title">{t.confirmSuccessTitle}</h1>
              <p className="checkout-confirm-copy">{t.confirmSuccessCopy}</p>
              <div className="checkout-confirm-actions">
                <a className="checkout-confirm-btn checkout-confirm-btn--primary" href={homeHref}>
                  {t.confirmBackHome}
                </a>
                {fromShop ? (
                  <a className="checkout-confirm-btn checkout-confirm-btn--ghost" href={shopHref}>
                    {siteT.shop.backToShop}
                  </a>
                ) : null}
              </div>
            </>
          ) : null}

          {phase === "error" ? (
            <>
              <p className="checkout-confirm-eyebrow">{t.confirmErrorEyebrow}</p>
              <h1 className="checkout-confirm-title">{t.confirmErrorTitle}</h1>
              <p className="checkout-confirm-copy">{detail || t.paymentError}</p>
              <div className="checkout-confirm-actions">
                <a
                  className="checkout-confirm-btn checkout-confirm-btn--primary"
                  href={checkoutHref}
                >
                  {t.confirmRetry}
                </a>
                <a className="checkout-confirm-btn checkout-confirm-btn--ghost" href={homeHref}>
                  {t.confirmBackHome}
                </a>
              </div>
            </>
          ) : null}
        </div>
      </main>

      <SiteFooter t={siteT.visitUs} />
    </div>
  );
}

export default CoffeeCheckoutConfirmPage;
